/**
 * A form's `lines` field (docs/design/lines-form-field.md, approved by Fable 2026-10-16 with
 * changes): a document's lines — an invoice's, a purchase request's, a timesheet's — declared as
 * columns of form fields and drawn by `LinesGrid`. What is decided here, once, for the form, the
 * shell and the tests alike: which lines are kept (empty trailing ones are dropped first, then
 * `min_rows` is checked), what each line sends (its editable columns, and `id` when the route sent
 * one), what a computed cell previews (rounded half away from zero at its decimals, as money is),
 * and what is refused before anything is sent.
 */
import { fieldProblem } from "./manifest.js";
export const LINE_CELL_TYPES = ['text', 'number', 'integer', 'date', 'select', 'boolean', 'computed'];
/** The columns a line holds, at most. */
export const LINE_COLUMNS_MAX = 8;
/** The lines a field holds when it does not say. */
export const LINE_ROWS_MAX = 500;
export const LINE_FORMULAS = ['multiply', 'sum', 'subtract', 'percent'];
export function isLinesField(field) {
    return field.type === 'lines';
}
const empty = (v) => v === undefined || v === null || v === '';
const isFigure = (type) => type === 'number' || type === 'integer';
/**
 * A figure rounded to `decimals`, half away from zero: 2.345 → 2.35, −2.345 → −2.35. Shifted by its
 * decimal exponent rather than multiplied, so 1.005 rounds to 1.01 and not to the 1.00 a float's
 * product would give — the rule `packages/fields` keeps for money.
 */
export function roundHalfAway(n, decimals) {
    if (!Number.isFinite(n))
        return n;
    const d = Math.max(0, Math.min(10, Math.trunc(decimals)));
    const [mantissa, exponent] = Math.abs(n).toExponential().split('e');
    const shifted = Math.round(Number(`${mantissa}e${Number(exponent) + d}`));
    const back = Number(`${shifted}e-${d}`);
    return n < 0 ? -back : back === 0 ? 0 : back;
}
/** The columns a formula reads. */
export function formulaInputs(of) {
    if (!of || typeof of !== 'object')
        return [];
    const [name] = Object.keys(of);
    const list = of[name ?? ''];
    return Array.isArray(list) ? list.filter((k) => typeof k === 'string') : [];
}
/**
 * A computed cell's figure for a line, or null while any input is empty or not a number. Rounded
 * half away from zero at the column's decimals (two when it says none). A preview: the route
 * recomputes it on save and what the route keeps is the figure.
 */
export function computeCell(column, line) {
    const of = column.of;
    if (!of)
        return null;
    const inputs = formulaInputs(of).map((k) => line[k]);
    if (!inputs.length || inputs.some((v) => empty(v) || !Number.isFinite(Number(v))))
        return null;
    const [a = 0, b = 0] = inputs.map(Number);
    let n;
    if ('multiply' in of)
        n = a * b;
    else if ('sum' in of)
        n = inputs.map(Number).reduce((x, y) => x + y, 0);
    else if ('subtract' in of)
        n = a - b;
    else if ('percent' in of)
        n = (a * b) / 100;
    else
        return null;
    return roundHalfAway(n, column.decimals ?? 2);
}
export function lineColumns(field) {
    return Array.isArray(field.columns) ? field.columns : [];
}
/** The columns a person edits and the line sends: every column but a computed one. */
export function editableColumns(field) {
    return lineColumns(field).filter((c) => c.type !== 'computed');
}
/** The fewest lines: `min_rows`, else one when the field is required, else none. */
export function linesMin(field) {
    return field.min_rows ?? (field.required ? 1 : 0);
}
export function linesMax(field) {
    return field.max_rows ?? LINE_ROWS_MAX;
}
/** A new line: each editable cell as `blank` says, a box unticked, the rest empty. */
export function blankLine(field) {
    return Object.fromEntries(editableColumns(field).map((c) => [c.key, field.blank?.[c.key] ?? (c.type === 'boolean' ? false : null)]));
}
/**
 * Whether a line holds nothing the person gave: every editable cell empty, or still what a new line
 * starts with (a quantity of one, an unticked box). Such a line at the end is dropped, never refused.
 */
export function isEmptyLine(field, line) {
    const start = blankLine(field);
    return editableColumns(field).every((c) => {
        const v = line[c.key];
        return empty(v) || Object.is(v, start[c.key]) || (c.type === 'boolean' && v === false);
    });
}
/** The value as lines: an array of objects, anything else as none. */
export function linesOf(value) {
    return Array.isArray(value) ? value.filter((l) => Boolean(l) && typeof l === 'object' && !Array.isArray(l)) : [];
}
/** The lines kept: the empty ones at the end dropped (only at the end — a gap in the middle is the person's). */
export function keptLines(field, value) {
    const lines = linesOf(value);
    let end = lines.length;
    while (end > 0 && isEmptyLine(field, lines[end - 1]))
        end -= 1;
    return lines.slice(0, end);
}
/** What the form opens with: the lines it was given (a row's), then blank lines up to `min_rows`. */
export function startLines(field, value) {
    const lines = linesOf(value);
    const pad = Math.max(0, linesMin(field) - lines.length);
    return [...lines, ...Array.from({ length: pad }, () => blankLine(field))];
}
/**
 * What the field sends: the kept lines, each with its editable columns only — a computed cell is
 * never sent — and the line's `id` when the route sent one, untouched, so the route can tell an
 * edited line from a new one. `id` is never a column.
 */
export function sentLines(field, value) {
    const keys = editableColumns(field).map((c) => c.key);
    return keptLines(field, value).map((line) => ({
        ...(line['id'] !== undefined && line['id'] !== null && line['id'] !== '' ? { id: line['id'] } : {}),
        ...Object.fromEntries(keys.map((k) => [k, line[k] === undefined || line[k] === '' ? null : line[k]])),
    }));
}
/**
 * The lines refused before anything is sent. Empty trailing lines are dropped first, then the
 * count is checked against `min_rows` and `max_rows`. Each cell of a kept line is checked as its
 * field type is — its format, pattern, length, range, a whole number — and, with `required`, an
 * empty required cell is one too (the form asks that only once the person has tried to send it).
 */
export function linesProblems(field, value, required = true) {
    const lines = keptLines(field, value);
    const cells = {};
    lines.forEach((line, i) => {
        for (const c of editableColumns(field)) {
            const v = line[c.key];
            const problem = empty(v) ? (required && c.required ? { key: 'form.required' } : null) : fieldProblem(c, v);
            if (problem)
                (cells[i] ??= {})[c.key] = problem;
        }
    });
    const min = linesMin(field);
    const max = linesMax(field);
    const whole = required && lines.length < min ? { key: 'lines.min', vars: { n: min } }
        : lines.length > max ? { key: 'lines.max', vars: { n: max } }
            : null;
    return { cells, whole };
}
/** The problem said under the grid: the count, else «correct the marked cells», else nothing. */
export function linesProblem(field, value) {
    const { cells, whole } = linesProblems(field, value);
    return whole ?? (Object.keys(cells).length ? { key: 'lines.cells' } : null);
}
/**
 * A server's refusal of one cell — `lines.3.quantity` (field, zero-based line, column) — as the
 * field, the line and the column; null for a path that names no cell of a `lines` field here.
 */
export function lineProblemPath(fields, path) {
    const m = /^([a-z][a-z0-9_]*)\.(\d+)\.([a-z][a-z0-9_]*)$/.exec(path);
    if (!m)
        return null;
    const field = fields.find((f) => f.key === m[1] && isLinesField(f));
    if (!field || !lineColumns(field).some((c) => c.key === m[3]))
        return null;
    return { field: m[1], line: Number(m[2]), column: m[3] };
}
/** The key a line column's choices are kept under beside the form's own: `lines.item_id`. */
export function lineChoiceKey(field, column) {
    return `${field}.${column}`;
}
/** A lines field with each select column's choices as they were read (by `lineChoiceKey`). */
export function withLineChoices(field, choices) {
    if (!isLinesField(field))
        return field;
    return { ...field, columns: lineColumns(field).map((c) => {
            const read = choices[lineChoiceKey(field.key, c.key)];
            return read ? { ...c, options: read } : c;
        }) };
}
/**
 * What is wrong with a `lines` field's declaration, one sentence each — the renderer's `inspect`
 * and the build's `loadUnit` refuse the same things. `headFields`: the keys of the other fields of
 * the form, the only names a column's `options_from.path` may read with `{name}`.
 */
export function linesDeclarationProblems(field, headFields) {
    const out = [];
    const at = `lines field "${field.key}"`;
    const columns = field.columns;
    if (!Array.isArray(columns) || !columns.length)
        return [`${at} declares no columns`];
    if (columns.length > LINE_COLUMNS_MAX)
        out.push(`${at} declares ${columns.length} columns, and a line holds at most ${LINE_COLUMNS_MAX}`);
    const keys = new Set();
    const types = new Map(columns.map((c) => [c?.key, c?.type]));
    const heads = new Set(headFields);
    for (const c of columns) {
        if (!c || typeof c.key !== 'string') {
            out.push(`${at} has a column with no key`);
            continue;
        }
        if (keys.has(c.key))
            out.push(`${at} names column "${c.key}" twice`);
        keys.add(c.key);
        if (c.key === 'id')
            out.push(`${at} has a column "id", which is the line's own and never a column`);
        if (!LINE_CELL_TYPES.includes(c.type))
            out.push(`${at}: column "${c.key}" is of type "${String(c.type)}", which a cell cannot hold`);
        if (c.type === 'computed') {
            const of = c.of;
            const names = formulaInputs(of);
            const kind = of && typeof of === 'object' ? Object.keys(of) : [];
            if (kind.length !== 1 || !LINE_FORMULAS.includes(kind[0]))
                out.push(`${at}: computed column "${c.key}" needs one of ${LINE_FORMULAS.join(', ')}`);
            else if (kind[0] === 'sum' ? names.length < 2 : names.length !== 2)
                out.push(`${at}: computed column "${c.key}" reads the wrong number of columns`);
            for (const n of names) {
                if (n === c.key)
                    out.push(`${at}: computed column "${c.key}" names itself`);
                else if (!isFigure(types.get(n)))
                    out.push(`${at}: computed column "${c.key}" reads "${n}", which is not a number column of this field`);
            }
        }
        else if (c.of !== undefined)
            out.push(`${at}: column "${c.key}" says how it is computed, but it is not a computed column`);
        if (c.total && !isFigure(c.type) && c.type !== 'computed')
            out.push(`${at}: column "${c.key}" is totalled, and only a figure adds up`);
        if (c.type === 'select' && !c.options?.length && !c.options_from)
            out.push(`${at}: select column "${c.key}" has no choices and no route to read them from`);
        if ((c.options || c.options_from) && c.type !== 'select')
            out.push(`${at}: column "${c.key}" offers choices, which only a select column takes`);
        for (const name of namedIn(c.options_from?.path)) {
            if (types.has(name))
                out.push(`${at}: column "${c.key}" reads its choices by "{${name}}", a column of the line; only the form's head fields may be named`);
            else if (!heads.has(name))
                out.push(`${at}: column "${c.key}" reads its choices by "{${name}}", which is not a field of this form`);
        }
    }
    const min = field.min_rows;
    const max = field.max_rows;
    if (min !== undefined && max !== undefined && min > max)
        out.push(`${at} asks for at least ${min} lines and at most ${max}`);
    for (const key of Object.keys(field.blank ?? {})) {
        if (!keys.has(key) || types.get(key) === 'computed')
            out.push(`${at}: blank names "${key}", which is not an editable column`);
    }
    return out;
}
/** The fields a `lines` field's own properties belong to: said on another type, they are read by nothing. */
export const LINES_ONLY = ['columns', 'min_rows', 'max_rows', 'blank', 'totals_label', 'totals_label_key'];
function namedIn(path) {
    return typeof path === 'string' ? [...path.matchAll(/\{([a-z][a-z0-9_]*)\}/g)].map((m) => m[1]) : [];
}
//# sourceMappingURL=lines.js.map